import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Check, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { IMAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "elt-admin-token";
const TABS = [
  { id: "journey", label: "Journey Requests" },
  { id: "corporate", label: "Corporate Accounts" },
  { id: "partner", label: "Partner Requests" },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-[var(--elt-line)] p-10">
        <img src={IMAGES.logo} alt="Europe Luxury Transfers" className="h-20 w-auto mx-auto mb-6" />
        <p className="eyebrow text-center mb-10">Private Access</p>
        <label className="elt-label">Email</label>
        <input data-testid="admin-email-input" className="elt-input mb-6" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="elt-label">Password</label>
        <input data-testid="admin-password-input" className="elt-input mb-8" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <p data-testid="admin-login-error" className="text-xs text-red-400 mb-4">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="btn-gold w-full justify-center disabled:opacity-50">Sign In</button>
      </form>
    </div>
  );
};

const DETAIL_FIELDS = {
  journey: [["pickup", "Pickup"], ["destination", "Destination"], ["date", "Date"], ["time", "Time"], ["passengers", "Passengers"], ["experience", "Experience"], ["vehicle", "Vehicle"], ["name", "Client"], ["email", "Email"], ["phone", "Phone"], ["luggage", "Luggage"], ["child_seats", "Child seats"], ["meet_greet", "Meet & greet"], ["multiple_stops", "Multiple stops"], ["language_preference", "Language"], ["notes", "Notes"]],
  corporate: [["company", "Company"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"], ["monthly_volume", "Monthly volume"], ["message", "Message"]],
  partner: [["company", "Company"], ["partner_type", "Type"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Phone"], ["message", "Message"]],
};

const summary = (kind, r) =>
  kind === "journey" ? `${r.pickup} → ${r.destination} · ${r.date} ${r.time}` : `${r.company} · ${r.contact_name}`;

const Row = ({ kind, r, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const handled = r.status === "handled";
  return (
    <div className={`border border-white/8 ${handled ? "opacity-55" : ""}`} data-testid={`admin-row-${r.id.slice(0, 8)}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors" data-testid={`admin-row-toggle-${r.id.slice(0, 8)}`}>
        <span className={`text-[0.58rem] tracking-[0.2em] uppercase px-2.5 py-1 border shrink-0 ${handled ? "border-white/15 text-[var(--elt-muted)]" : "border-[var(--elt-gold)] text-[var(--elt-gold-light)]"}`}>{handled ? "Handled" : "New"}</span>
        <span className="text-[0.62rem] tracking-[0.18em] text-[var(--elt-gold)]/80 shrink-0">{r.id.slice(0, 8).toUpperCase()}</span>
        <span className="text-sm font-light text-[var(--elt-ivory)]/90 truncate flex-1">{summary(kind, r)}</span>
        <span className="text-[0.62rem] text-[var(--elt-muted)] shrink-0 hidden sm:block">{new Date(r.created_at).toLocaleString()}</span>
        {open ? <ChevronUp size={14} className="text-[var(--elt-muted)]" /> : <ChevronDown size={14} className="text-[var(--elt-muted)]" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/5">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-2 py-4">
            {DETAIL_FIELDS[kind].map(([k, label]) => {
              const v = r[k];
              if (v === "" || v === undefined || v === null || v === false) return null;
              return (
                <div key={k} className="flex gap-3 text-sm py-1 border-b border-white/5">
                  <span className="text-[0.62rem] tracking-[0.15em] uppercase text-[var(--elt-muted)] w-32 shrink-0 pt-0.5">{label}</span>
                  <span className="font-light text-[var(--elt-ivory)]/90 break-all">{v === true ? "Yes" : String(v)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid={`admin-status-btn-${r.id.slice(0, 8)}`} onClick={() => onStatus(r, handled ? "new" : "handled")}
              className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.2em] uppercase border border-[var(--elt-gold)]/50 text-[var(--elt-gold-light)] px-4 py-2 hover:border-[var(--elt-gold)] transition-colors">
              {handled ? <><RotateCcw size={12} /> Mark as new</> : <><Check size={12} /> Mark as handled</>}
            </button>
            <button data-testid={`admin-delete-btn-${r.id.slice(0, 8)}`} onClick={() => onDelete(r)}
              className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.2em] uppercase border border-red-500/40 text-red-400/90 px-4 py-2 hover:border-red-400 transition-colors">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("journey");
  const [data, setData] = useState({ journey: [], corporate: [], partner: [] });
  const [loading, setLoading] = useState(false);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c, p] = await Promise.all(
        ["journey", "corporate", "partner"].map((k) => axios.get(`${API}/requests/${k}`, { headers: authHeaders() }))
      );
      setData({ journey: j.data, corporate: c.data, partner: p.data });
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const check = async () => {
      if (!localStorage.getItem(TOKEN_KEY)) { setChecking(false); return; }
      try {
        const { data: me } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        setUser(me);
      } catch { localStorage.removeItem(TOKEN_KEY); }
      setChecking(false);
    };
    check();
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const onStatus = async (r, status) => {
    await axios.patch(`${API}/requests/${tab}/${r.id}/status`, { status }, { headers: authHeaders() });
    setData((d) => ({ ...d, [tab]: d[tab].map((x) => (x.id === r.id ? { ...x, status } : x)) }));
  };

  const onDelete = async (r) => {
    if (!window.confirm("Delete this request permanently?")) return;
    await axios.delete(`${API}/requests/${tab}/${r.id}`, { headers: authHeaders() });
    setData((d) => ({ ...d, [tab]: d[tab].filter((x) => x.id !== r.id) }));
    toast.success("Request deleted");
  };

  if (checking) return <div className="min-h-screen" />;
  if (!user) return <Login onLogin={setUser} />;

  const rows = data[tab];
  const newCount = (k) => data[k].filter((x) => x.status !== "handled").length;

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 bg-[#0B0C0E]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="Europe Luxury Transfers" className="h-12 w-auto" />
            <span className="eyebrow hidden sm:block">Operations Dashboard</span>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase text-[var(--elt-muted)] hover:text-[var(--elt-ivory)] transition-colors">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-2 border-b border-white/8 mb-10">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-5 py-4 text-[0.68rem] tracking-[0.22em] uppercase border-b -mb-px transition-colors ${tab === t.id ? "text-[var(--elt-gold-light)] border-[var(--elt-gold)]" : "text-[var(--elt-muted)] border-transparent hover:text-[var(--elt-ivory)]"}`}>
              {t.label} {newCount(t.id) > 0 && <span className="ml-1 text-[var(--elt-gold)]">({newCount(t.id)})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm font-light text-[var(--elt-muted)]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm font-light text-[var(--elt-muted)]" data-testid="admin-empty-state">No requests yet.</p>
        ) : (
          <div className="space-y-3" data-testid="admin-request-list">
            {rows.map((r) => <Row key={r.id} kind={tab} r={r} onStatus={onStatus} onDelete={onDelete} />)}
          </div>
        )}
      </div>
    </div>
  );
}
